import type { DragEvent, ReactNode } from "react";

export interface TileProps {
  title: ReactNode;
  /** Right-aligned header controls (e.g. a hide button + drag handle in edit mode). */
  actions?: ReactNode;
  className?: string;
  /** Visually marks the tile as part of an editable layout (grab cursor, dashed affordance). */
  editing?: boolean;
  draggable?: boolean;
  /** This tile is the one currently being dragged. */
  dragging?: boolean;
  /** A dragged tile is hovering over this one (drop indicator). */
  dropTarget?: boolean;
  onDragStart?: (e: DragEvent<HTMLElement>) => void;
  onDragOver?: (e: DragEvent<HTMLElement>) => void;
  onDragLeave?: (e: DragEvent<HTMLElement>) => void;
  onDrop?: (e: DragEvent<HTMLElement>) => void;
  onDragEnd?: (e: DragEvent<HTMLElement>) => void;
  children?: ReactNode;
}

/**
 * A titled card — the `fui-tile` vocabulary. The reusable building block for tile/dashboard layouts
 * (animal detail, settings dashboard, …). Presentational only: it renders a header (title + optional
 * actions) and a body, and forwards native drag-and-drop handlers so a parent board can make it
 * re-orderable. It owns no layout/persistence state — see {@link TileBoard} for orchestration.
 */
export function Tile({
  title,
  actions,
  className,
  editing,
  draggable,
  dragging,
  dropTarget,
  children,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
}: TileProps) {
  const classes = ["fui-tile"];
  if (editing) classes.push("fui-tile--editing");
  if (dragging) classes.push("fui-tile--dragging");
  if (dropTarget) classes.push("fui-tile--drop-target");
  if (className) classes.push(className);

  return (
    <section
      className={classes.join(" ")}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
    >
      <header className="fui-tile__header">
        <h4 className="fui-tile__title">{title}</h4>
        {actions && <div className="fui-tile__actions">{actions}</div>}
      </header>
      {children}
    </section>
  );
}
